import { useForm } from "@formspree/react";
import { useContextProvider } from "../../../context";
import { bookingDict } from "../../../utilities";
import successIcon from "../../../icons/ok.png";
import errorIcon from "../../../icons/stop.png";
import { Car, CarPriceRange, Language } from "../../../types";
import { useEffect, useState } from "react";
import { trackEvent } from "../../../utilities/tracking";
import { Spinner } from "../../spinner";
import "./index.css";

interface BookingFormProps {
  car: Car;
}

const getInitialState = (car: Car, language: Language) => {
  return {
    carName: car.make + " " + car.model,
    fullName: "",
    email: "",
    phone: "",
    pickUpPlace: "Alicante",
    dropOffPlace: "Alicante",
    pickUpDate: "",
    pickUpTime: "12:00",
    dropOffDate: "",
    dropOffTime: "12:00",
    olderThen24: true,
    drivingExpirience: true,
    message: "",
    numberOfDays: 0,
    totalPrice: 0,
    dailyPrice: 0,
    deposit: car.deposit,
    language,
  };
};

export const BookingForm: React.FC<BookingFormProps> = ({ car }) => {
  const { language } = useContextProvider();
  const [formState, setFormState] = useState(getInitialState(car, language));
  const [state, handleSubmit, reset] = useForm("mqezqekq");

  const submittedSuccessfully = state.succeeded;
  const hasError = !!state.errors;

  useEffect(() => {
    if (formState.pickUpDate && formState.dropOffDate) {
      const numberOfDays = Math.ceil(
        (new Date(formState.dropOffDate).getTime() -
          new Date(formState.pickUpDate).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      if (numberOfDays > 0) {
        setFormState({ ...formState, numberOfDays });
      } else {
        setFormState({
          ...formState,
          numberOfDays: 0,
          totalPrice: 0,
          dailyPrice: 0,
          pickUpDate: "",
          dropOffDate: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.pickUpDate, formState.dropOffDate]);

  useEffect(() => {
    const days = formState.numberOfDays;
    if (days > 0) {
      const dailyPrice =
        days === 1
          ? car.price[CarPriceRange.OneDay]
          : days < 4
            ? car.price[CarPriceRange.ThreeDays]
            : days < 9
              ? car.price[CarPriceRange.EightDays]
              : days < 26
                ? car.price[CarPriceRange.TwentyFiveDays]
                : car.price[CarPriceRange.LongTerm];

      setFormState({
        ...formState,
        dailyPrice,
        totalPrice: dailyPrice * days,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.numberOfDays, car.price]);

  const handleChange = ({
    target: { name, value, type },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [name]:
        type === "checkbox"
          ? !formState[name as keyof typeof formState]
          : value,
    });
  };

  const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(formState);
    trackEvent("booking_form_submitt", {
      event_category: "form",
      event_label: "booking_form",
    });
  };

  const form = (
    <form onSubmit={handleSubmitForm}>
      <div className="BookingForm-column">
        <label htmlFor="carName">{bookingDict[language].carName}</label>
        <input
          type="text"
          id="carName"
          name="carName"
          autoComplete="carName"
          value={formState.carName}
          onChange={handleChange}
          disabled
        />
      </div>
      <div className="BookingForm-column">
        <label htmlFor="fullName">{bookingDict[language].fullName}</label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          autoComplete="name"
          placeholder={bookingDict[language].fullNamePlaceholder}
          required
          value={formState.fullName}
          onChange={handleChange}
        />
      </div>
      <div className="BookingForm-column">
        <label htmlFor="email">{bookingDict[language].email}</label>
        <input
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          placeholder={bookingDict[language].emailPlaceholder}
          value={formState.email}
          onChange={handleChange}
        />
      </div>
      <div className="BookingForm-column">
        <label htmlFor="phone">{bookingDict[language].phone}</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          autoComplete="tel"
          placeholder={bookingDict[language].phonePlaceholder}
          required
          value={formState.phone}
          onChange={handleChange}
        />
      </div>
      <div className="BookingForm-column">
        <label htmlFor="pickUpPlace">{bookingDict[language].pickUpPlace}</label>
        <input
          type="text"
          id="pickUpPlace"
          name="pickUpPlace"
          placeholder={bookingDict[language].pickUpPlacePlaceholder}
          required
          value={formState.pickUpPlace}
          onChange={handleChange}
        />
      </div>
      <div className="BookingForm-column">
        <label htmlFor="dropOffPlace">
          {bookingDict[language].dropOffPlace}
        </label>
        <input
          type="text"
          id="dropOffPlace"
          name="dropOffPlace"
          placeholder={bookingDict[language].dropOffPlacePlaceholder}
          required
          value={formState.dropOffPlace}
          onChange={handleChange}
        />
      </div>
      <div className="BookingForm-row">
        <div className="BookingForm-column">
          <label htmlFor="pickUpDate">{bookingDict[language].pickUpDate}</label>
          <input
            type="date"
            id="pickUpDate"
            name="pickUpDate"
            required
            value={formState.pickUpDate}
            onChange={handleChange}
          />
        </div>
        <div className="BookingForm-column">
          <label htmlFor="pickUpTime">{bookingDict[language].pickUpTime}</label>
          <input
            type="time"
            id="pickUpTime"
            name="pickUpTime"
            value={formState.pickUpTime}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="BookingForm-row">
        <div className="BookingForm-column">
          <label htmlFor="dropOffDate">
            {bookingDict[language].dropOffDate}
          </label>
          <input
            type="date"
            id="dropOffDate"
            name="dropOffDate"
            required
            value={formState.dropOffDate}
            onChange={handleChange}
          />
        </div>
        <div className="BookingForm-column">
          <label htmlFor="dropOffTime">
            {bookingDict[language].dropOffTime}
          </label>
          <input
            type="time"
            id="dropOffTime"
            name="dropOffTime"
            value={formState.dropOffTime}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="BookingForm-checkbox">
        <input
          type="checkbox"
          id="olderThen24"
          name="olderThen24"
          required
          checked={formState.olderThen24}
          onChange={handleChange}
        />
        <label htmlFor="olderThen24">{bookingDict[language].olderThen24}</label>
      </div>
      <div className="BookingForm-checkbox">
        <input
          type="checkbox"
          id="drivingExpirience"
          name="drivingExpirience"
          checked={formState.drivingExpirience}
          onChange={handleChange}
          required
        />
        <label htmlFor="drivingExpirience">
          {bookingDict[language].drivingExperience}
        </label>
      </div>

      <div className="BookingForm-column">
        <label htmlFor="message">{bookingDict[language].messageOptional}</label>
        <textarea
          id="message"
          name="message"
          placeholder={bookingDict[language].messageOptionalPlaceholder}
        />
      </div>
      {formState.numberOfDays > 0 && (
        <>
          <div className="BookingForm-column">
            <label>
              {bookingDict[language].numberOfDays} {formState.numberOfDays}
            </label>
          </div>
          <div className="BookingForm-column">
            <label>
              {bookingDict[language].dailyPrice} {formState.dailyPrice} €
            </label>
          </div>
          <div className="BookingForm-column">
            <label>
              {bookingDict[language].totalPrice} {formState.totalPrice} €
            </label>
          </div>
          <div className="BookingForm-column">
            <label>
              {bookingDict[language].deposit} {formState.deposit} €
            </label>
          </div>
          <div className="BookingForm-column">
            <label>
              <strong>
                {bookingDict[language].paymentAmount}{" "}
                {formState.deposit + formState.totalPrice} €
              </strong>
            </label>
          </div>
          <div className="BookingForm-column">
            <label className="BookingForm-attentionText">
              ⚠️ {bookingDict[language].attentionText}
            </label>
          </div>
        </>
      )}

      {state.submitting ? (
        <Spinner />
      ) : (
        <button
          className="BookingForm-button"
          type="submit"
          disabled={state.submitting}
        >
          {bookingDict[language].bookCar}
        </button>
      )}
    </form>
  );

  const error = (
    <>
      <div className="BookingForm-message">
        <img src={errorIcon} alt="Error Icon" />
        <p>
          <strong>{bookingDict[language].erorr.title}</strong>
        </p>
        <p>{bookingDict[language].erorr.text}</p>
      </div>
      <button className="BookingForm-button" type="button" onClick={reset}>
        {bookingDict[language].repeat}
      </button>
    </>
  );

  const success = (
    <>
      <div className="BookingForm-message">
        <img src={successIcon} alt="Success Icon" />
        <p>
          <strong>{bookingDict[language].bookingSuccess.title}</strong>
        </p>
        <p>{bookingDict[language].bookingSuccess.text}</p>
      </div>
    </>
  );

  return (
    <div className="BookingForm">
      <h2>{bookingDict[language].bookingFormTitle}</h2>
      {hasError ? error : submittedSuccessfully ? success : form}
    </div>
  );
};
